<template>
  <main class="physical-page">
    <AppNavBar
      backgroundColor="#fff"
      :title="t('wallet.activatePhysicalCard')"
    />

    <section class="physical-content">
      <section class="activation-guide">
        <img
          class="activation-guide__background"
          :src="physicalActiveBanner"
          alt=""
        />
        <div class="activation-guide__content">
          <img
            class="activation-guide__icon"
            :src="physicalActiveCardIcon"
            alt=""
          />
          <div class="activation-guide__copy">
            <p class="activation-guide__title">
              {{ t("wallet.activatePhysicalGuideTitle") }}
            </p>
            <p class="activation-guide__desc">
              {{ t("wallet.activatePhysicalGuideDesc") }}
            </p>
          </div>
        </div>
      </section>

      <section class="form-section">
        <div class="field-group">
          <label class="field-label">{{ t("wallet.cardNumber") }}</label>
          <van-field
            v-model="cardNum"
            type="tel"
            class="physical-input"
            :placeholder="t('wallet.enterCardNumber')"
          />
        </div>

        <div class="field-group">
          <label class="field-label">{{ t("card.validThroughLabel") }}</label>
          <van-field
            :model-value="validThru"
            readonly
            clickable
            class="physical-input"
            :placeholder="t('wallet.enterValidThrough')"
            @click="openValidThruPicker"
          />
        </div>

        <div class="field-group">
          <label class="field-label">{{ t("card.cvvCode") }}</label>
          <van-field
            v-model="cvv"
            type="tel"
            maxlength="4"
            class="physical-input"
            :placeholder="t('wallet.enterCvv')"
          />
        </div>
      </section>
    </section>

    <footer class="submit-bar">
      <van-button
        block
        round
        type="primary"
        class="submit-btn"
        :loading="submitting"
        :disabled="!canSubmit"
        @click="handleNext"
      >
        {{ t("auth.next") }}
      </van-button>
    </footer>

    <van-popup v-model:show="showValidThruPicker" position="bottom">
      <van-date-picker
        v-model="validThruPickerValue"
        class="valid-thru-date-picker"
        :columns-type="['year', 'month']"
        :title="t('card.validThroughLabel')"
        :formatter="formatter"
        :confirm-button-text="t('common.confirm')"
        :cancel-button-text="t('common.cancel')"
        @confirm="confirmValidThru"
        @cancel="showValidThruPicker = false"
      />
    </van-popup>

    <PayPasswordPopup
      v-model:show="showPasswordDialog"
      v-model:password="passwordInput"
      :title="t('auth.verifyWalletPassword')"
      :placeholder="t('auth.enterPassword')"
      :confirm-text="t('common.confirm')"
      :forgot-text="t('auth.forgotPasswordRecovery')"
      show-forgot
      :confirm-disabled="!passwordInput.trim()"
      @cancel="closePasswordDialog(false)"
      @closed="handlePasswordPopupClosed"
      @forgot="handleForgotPassword"
      @confirm="submitPasswordDialog"
    />

    <PhysicalSecondCheckPopup
      v-model:show="showSecondCheckPopup"
      :confirm-loading="submitting"
      @sms-sent="handleSecondCheckSmsSent"
      @confirm="confirmSecondCheck"
    />
  </main>
</template>

<script setup>
import { computed, onBeforeUnmount, ref } from "vue";
import { closeToast, showFailToast, showLoadingToast } from "vant";
import { showPassedToast } from "@/utils/toast";
import { useI18n } from "vue-i18n";
import { useRouter } from "vue-router";
import PayPasswordPopup from "@/components/PayPasswordPopup.vue";
import PhysicalSecondCheckPopup from "@/components/PhysicalSecondCheckPopup.vue";
import { payPwdCheck } from "@/api/auth";
import { activePhysicalCard } from "@/api/card";
import { useProfileStore } from "@/stores/profile";
import { navigateWithState } from "@/utils/route-state";
import physicalActiveBanner from "@/assets/images/cardImage/physical-active-banner.png";
import physicalActiveCardIcon from "@/assets/images/cardImage/physical-active-card-icon.png";

const { t } = useI18n();
const router = useRouter();
const profileStore = useProfileStore();

const cardNum = ref("");
const validThru = ref("");
const cvv = ref("");
const showValidThruPicker = ref(false);
const validThruPickerValue = ref([]);
const showPasswordDialog = ref(false);
const passwordInput = ref("");
const showSecondCheckPopup = ref(false);
const submitting = ref(false);
let passwordResolver = null;

const canSubmit = computed(() => Boolean(String(cardNum.value || "").trim()));

const validThruMinDate = new Date();
const validThruMaxDate = new Date(validThruMinDate.getFullYear() + 20, 11, 31);

const formatter = (type, option) => {
  if (type === "year") {
    option.text += "年";
  }
  if (type === "month") {
    option.text += "月";
  }
  return option;
};

function openValidThruPicker() {
  const [year, month] = String(validThru.value || "").split("/");
  const currentDate = new Date();
  const selectedYear = year
    ? String(Number(year))
    : String(currentDate.getFullYear());
  const selectedMonth = month
    ? String(Number(month)).padStart(2, "0")
    : String(currentDate.getMonth() + 1).padStart(2, "0");
  validThruPickerValue.value = [selectedYear, selectedMonth];
  showValidThruPicker.value = true;
}

function confirmValidThru({ selectedValues }) {
  const [year, month] = selectedValues;
  validThru.value = `${String(year)}/${String(month).padStart(2, "0")}`;
  showValidThruPicker.value = false;
}

function verifyWalletPassword() {
  passwordInput.value = "";
  showPasswordDialog.value = true;
  return new Promise((resolve) => {
    passwordResolver = resolve;
  });
}

function closePasswordDialog(result = false) {
  if (passwordResolver) {
    passwordResolver(result);
    passwordResolver = null;
  }
  showPasswordDialog.value = false;
}

function handlePasswordPopupClosed() {
  passwordInput.value = "";
}

function handleForgotPassword() {
  closePasswordDialog(false);
  router.push("/auth/forgot-pay-password");
}

function goActivatedCardIndex() {
  navigateWithState(router, "/card/index", {
    namespace: "home-card",
    replace: true,
    payload: {
      tab: "physical",
    },
  });
}

async function handleNext() {
  if (!canSubmit.value || submitting.value) {
    return;
  }

  const verified = await verifyWalletPassword();
  if (!verified) {
    return;
  }

  await activateCard();
}

async function submitPasswordDialog() {
  const payPwd = String(passwordInput.value || "").trim();
  if (!payPwd) {
    showFailToast(t("auth.enterPassword"));
    return;
  }

  const result = await payPwdCheck({ payPwd });
  if (!result) {
    showFailToast(t("auth.passwordError"));
    return;
  }

  closePasswordDialog(true);
}

function handleSecondCheckSmsSent() {
  navigateWithState(router, "/wallet/open-card/history/detail", {
    namespace: "card-open-history-detail",
    payload: {
      record: {
        physical: true,
        cardNum: String(cardNum.value || "").trim(),
        applyTime: Date.now(),
      },
      status: "pending",
    },
  });
}

async function confirmSecondCheck({ smsCode } = {}) {
  if (!String(smsCode || "").trim() || submitting.value) {
    return;
  }

  submitting.value = true;
  showLoadingToast({
    message: t("common.submitting"),
    forbidClick: true,
    duration: 0,
  });
  try {
    await activePhysicalCard({
      address: profileStore.platformAddress || null,
      cardNum: String(cardNum.value || "").trim(),
      validThru: String(validThru.value || "").trim(),
      cvv: String(cvv.value || "").trim(),
      smsCode: String(smsCode || "").trim(),
    });
    showSecondCheckPopup.value = false;
    closeToast();
    showPassedToast(t("wallet.activateSuccess"));
    setTimeout(() => {
      goActivatedCardIndex();
    }, 500);
  } catch (error) {
    closeToast();
    showFailToast(error?.message || error?.msg || t("common.retryLater"));
  } finally {
    submitting.value = false;
  }
}

async function activateCard() {
  if (!profileStore.platformAddress) {
    router.replace("/login");
    return;
  }

  submitting.value = true;
  showLoadingToast({
    message: t("common.submitting"),
    forbidClick: true,
    duration: 0,
  });
  try {
    await activePhysicalCard({
      address: profileStore.platformAddress || null,
      cardNum: String(cardNum.value || "").trim(),
      validThru: String(validThru.value || "").trim(),
      cvv: String(cvv.value || "").trim(),
    });
    closeToast();
    showPassedToast(t("wallet.activateSuccess"));
    setTimeout(() => {
      goActivatedCardIndex();
    }, 500);
  } catch (error) {
    closeToast();
    showFailToast(error?.message || error?.msg || t("common.retryLater"));
  } finally {
    submitting.value = false;
  }
}

onBeforeUnmount(() => {
  closePasswordDialog(false);
});
</script>

<style scoped lang="scss">
.physical-page {
  min-height: var(--app-viewport-height);
  background: #ffffff;
}

.physical-content {
  padding: 14px 16px calc(98px + env(safe-area-inset-bottom, 0px));
}

.activation-guide {
  position: relative;
  padding: 22px 0;
  overflow: hidden;
  border-radius: 12px;
}

.activation-guide__background {
  position: absolute;
  inset: 0;
  width: 100%;
  height: 100%;
  object-fit: fill;
}

.activation-guide__content {
  position: relative;
  display: flex;
  height: 100%;
  align-items: center;
  gap: 9px;
  padding: 0 15px;
}

.activation-guide__icon {
  width: 48px;
  height: 48px;
  flex: 0 0 48px;
  object-fit: contain;
}

.activation-guide__copy {
  min-width: 0;
}

.activation-guide__title,
.activation-guide__desc {
  margin: 0;
}

.activation-guide__title {
  color: #2667ff;
  font-size: 16px;
  font-weight: 600;
  line-height: 16px;
}

.activation-guide__desc {
  margin-top: 8px;
  color: #747b88;
  font-size: 12px;
  line-height: 12px;
}

.form-section {
  margin-top: 38px;
}

.field-group {
  margin-bottom: 20px;
}

.field-label {
  display: block;
  margin-bottom: 10px;
  color: #333333;
  font-size: 16px;
  line-height: 16px;
  font-weight: 500;
}

.physical-input {
  height: 42px;
  padding: 0;
  border-radius: 10px;
  background: #f4f5f6;

  :deep(.van-field__body) {
    height: 42px;
    padding: 0 16px;
  }

  :deep(.van-field__control) {
    color: #333333;
    font-size: 14px;
    line-height: 14px;
  }

  :deep(.van-field__control::placeholder) {
    color: #666666;
  }
}

.submit-bar {
  position: fixed;
  right: 0;
  bottom: 0;
  left: 0;
  z-index: 10;
  padding: 10px 16px calc(10px + env(safe-area-inset-bottom, 0px));
  background: #ffffff;
  box-shadow: 4px 4px 7.5px rgba(0, 0, 0, 0.3);
}

.submit-btn {
  height: 44px;
  border: 0;
  background: #2667ff;

  &:disabled {
    opacity: 1;
    background: #e0e0e0;
    color: #999999;
  }

  :deep(.van-button__text) {
    font-size: 16px;
    font-weight: 600;
  }
}

:global(.valid-thru-date-picker .van-hairline-unset--top-bottom::after) {
  border-width: 0;
}
:global(.valid-thru-date-picker .van-picker__frame) {
  background: #e2edff;
  border: 1px solid #d7e9ff;
  z-index: 1;
  border-radius: 12px;
}

:global(.valid-thru-date-picker .van-picker-column__item--selected) {
  color: #2667ff;
  font-size: 20px;
  font-weight: 700;
}

:global(.valid-thru-date-picker .van-picker-column) {
  position: relative;
  z-index: 2;
}

:global(.valid-thru-date-picker .van-picker__mask) {
  z-index: 3;
}

</style>
